import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Settings } from "lucide-react";
import { SanityIntegration } from "@/types/integration";
import { SanityService } from "@/services/sanity";

interface SanitySettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  brandId: string;
  integration: SanityIntegration;
}

export function SanitySettingsDialog({
  open,
  onOpenChange,
  onSuccess,
  brandId,
  integration,
}: SanitySettingsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    autoPublish: integration.settings?.autoPublish ?? true,
    selectedDocumentType: integration.settings?.selectedDocumentType || "post",
    imageFieldName: integration.settings?.imageFieldName || "mainImage",
    defaultAuthor: integration.settings?.defaultAuthor || "",
    defaultPublishOptions: {
      returnIds: integration.settings?.defaultPublishOptions?.returnIds ?? true,
      returnDocuments:
        integration.settings?.defaultPublishOptions?.returnDocuments ?? false,
      visibility:
        integration.settings?.defaultPublishOptions?.visibility || "sync",
      dryRun: integration.settings?.defaultPublishOptions?.dryRun ?? false,
    },
  });

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await SanityService.updateSettings({
        brandId,
        autoPublish: settings.autoPublish,
        selectedDocumentType: settings.selectedDocumentType,
        imageFieldName: settings.imageFieldName,
        defaultAuthor: settings.defaultAuthor,
        defaultPublishOptions: settings.defaultPublishOptions,
      });

      toast.success("Sanity settings updated successfully");
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating Sanity settings:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update Sanity settings",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sanity Settings</DialogTitle>
          <DialogDescription>
            Configure your Sanity publishing preferences
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Auto-publish toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-publish">Auto-publish articles</Label>
              <p className="text-sm text-muted-foreground">
                Automatically publish articles to Sanity when they're created
              </p>
            </div>
            <Switch
              id="auto-publish"
              checked={settings.autoPublish}
              onCheckedChange={(checked) =>
                setSettings((prev) => ({
                  ...prev,
                  autoPublish: checked,
                }))
              }
            />
          </div>

          {/* Document type */}
          <div className="space-y-2">
            <Label htmlFor="document-type">Document Type</Label>
            <Input
              id="document-type"
              placeholder="e.g., post, article, blogPost"
              value={settings.selectedDocumentType}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  selectedDocumentType: e.target.value,
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Sanity document type to use for published articles
            </p>
          </div>

          {/* Image field name */}
          <div className="space-y-2">
            <Label htmlFor="image-field">Image Field Name</Label>
            <Input
              id="image-field"
              placeholder="e.g., mainImage, heroImage, featuredImage"
              value={settings.imageFieldName}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  imageFieldName: e.target.value,
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Field name for the featured image in your Sanity schema
            </p>
          </div>

          {/* Default author */}
          <div className="space-y-2">
            <Label htmlFor="default-author">Default Author</Label>
            <Input
              id="default-author"
              placeholder="e.g., John Doe or author document ID"
              value={settings.defaultAuthor}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  defaultAuthor: e.target.value,
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Default author to assign to published articles (name or reference ID)
            </p>
          </div>

          {/* Publishing options */}
          <div className="space-y-4">
            <Label>Publishing Options</Label>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="return-ids"
                  checked={settings.defaultPublishOptions.returnIds}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({
                      ...prev,
                      defaultPublishOptions: {
                        ...prev.defaultPublishOptions,
                        returnIds: !!checked,
                      },
                    }))
                  }
                />
                <Label htmlFor="return-ids" className="text-sm">
                  Return document IDs
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="return-documents"
                  checked={settings.defaultPublishOptions.returnDocuments}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({
                      ...prev,
                      defaultPublishOptions: {
                        ...prev.defaultPublishOptions,
                        returnDocuments: !!checked,
                      },
                    }))
                  }
                />
                <Label htmlFor="return-documents" className="text-sm">
                  Return full documents
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="dry-run"
                  checked={settings.defaultPublishOptions.dryRun}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({
                      ...prev,
                      defaultPublishOptions: {
                        ...prev.defaultPublishOptions,
                        dryRun: !!checked,
                      },
                    }))
                  }
                />
                <Label htmlFor="dry-run" className="text-sm">
                  Dry run (validate only)
                </Label>
              </div>
            </div>
          </div>

          {/* Visibility mode */}
          <div className="space-y-2">
            <Label htmlFor="visibility">Visibility Mode</Label>
            <Select
              value={settings.defaultPublishOptions.visibility}
              onValueChange={(value: "sync" | "async" | "deferred") =>
                setSettings((prev) => ({
                  ...prev,
                  defaultPublishOptions: {
                    ...prev.defaultPublishOptions,
                    visibility: value,
                  },
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sync">Sync (Immediate)</SelectItem>
                <SelectItem value="async">Async (Background)</SelectItem>
                <SelectItem value="deferred">Deferred (Batch)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Controls how mutations are processed by Sanity
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveSettings} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SanitySettingsButtonProps {
  brandId: string;
  integration: SanityIntegration;
  onSuccess: () => void;
}

export function SanitySettingsButton({
  brandId,
  integration,
  onSuccess,
}: SanitySettingsButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2"
      >
        <Settings className="h-4 w-4" />
        Settings
      </Button>
      <SanitySettingsDialog
        open={open}
        onOpenChange={setOpen}
        onSuccess={onSuccess}
        brandId={brandId}
        integration={integration}
      />
    </>
  );
}
